import React, { useEffect, useState } from 'react';
import AppInput from '../../../../../base/input';
import { useFormik } from 'formik';
import {
  useBeneficiaryStore,
  useRateStore,
  useResourceStore,
} from '../../../../../../store';
import { FiCheck } from 'react-icons/fi';
import AppButton from '../../../../../base/button';
import beneficiarySchemas from '../../../../../../constants/schemas/beneficiary';
import CustomDropdown from '../../../../../base/custom-dropdown';
import toast from 'react-hot-toast';
import Loader from '../../../../../base/loader';
import usePayoutStore from '../../../../../../store/payout';

interface AddIbanBeneficiaryProps {
  addBeneficiary: () => void;
  prev: () => void;
  loading: boolean;
  caller?: string;
}

const AddIbanBeneficiary: React.FC<AddIbanBeneficiaryProps> = ({
  addBeneficiary,
  prev,
  loading,
  caller,
}) => {
  const {
    setBeneficiaryPayload,
    beneficiaryPayload,
    validateIban,
    loading: validating,
  } = useBeneficiaryStore();
  const { beneficiaryFormState } = usePayoutStore();
  const { loading: loadingResource } = useResourceStore();
  const { getActiveCountries, activeCountries: countries } = useRateStore();

  const [isIbanValid, setIsIbanValid] = useState(false);

  const [bankAddressCountryTouched, setBankAddressCountryTouched] =
    React.useState(false);

  const formik = useFormik({
    initialValues: {
      countryCode: beneficiaryPayload.ibanPayment?.countryCode ?? '',
      bic: beneficiaryPayload.ibanPayment?.bic ?? '',
      ibanAccount: beneficiaryPayload.ibanPayment?.ibanAccount ?? '',
      bankAddressCountryName: '',
    },
    validationSchema: beneficiarySchemas[3],
    validateOnMount: true,
    onSubmit() {},
  });

  const disableForm = () => {
    return (
      !formik.values.countryCode ||
      !formik.values.bic ||
      !formik.values.ibanAccount ||
      !isIbanValid
    );
  };

  useEffect(() => {
    getActiveCountries();
  }, []);

  useEffect(() => {
    if (bankAddressCountryTouched) {
      formik.validateForm();
    }
  }, [formik.values.countryCode]);

  const handleCountrySelect = (value: any) => {
    const countryCode = value.name.toUpperCase();
    formik.setFieldValue('bankAddressCountryName', value.label);
    formik.setFieldValue('countryCode', countryCode, true);
    if (
      formik.values.ibanAccount &&
      !formik.values.ibanAccount.startsWith(countryCode)
    ) {
      formik.setFieldValue('ibanAccount', '', true);
    }
    setBankAddressCountryTouched(true);
    formik.setFieldTouched('countryCode', true);
  };

  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    value = value.replace(/\s/g, '');
    formik.setFieldValue('ibanAccount', value, true);
  };

  const submitForm = () => {
    setBeneficiaryPayload({
      ...beneficiaryPayload,
      ibanPayment: {
        countryCode: formik.values.countryCode,
        bic: formik.values.bic,
        ibanAccount: formik.values.ibanAccount,
      },
    });
    addBeneficiary();
  };

  return (
    <div>
      <form
        onSubmit={formik.handleSubmit}
        action="#"
        className="flex flex-col gap-8"
      >
        <div>
          {caller !== 'payout' && (
            <div className="flex items-center text-sm font-semibold mb-6">
              <span className="mr-4 text-gray-500">IBAN Account Details</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>
          )}

          <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
            <CustomDropdown
              label="Select Country"
              options={countries.map((country) => {
                return {
                  name: country.iso2,
                  label: country.name,
                  countryFlag: country.flagUrl,
                };
              })}
              selectedValue={formik.values.bankAddressCountryName}
              onSelectChange={handleCountrySelect}
              emitObject
              required
              showCountryFlag
              loading={loadingResource}
              errorMessage={
                !formik.values.countryCode && bankAddressCountryTouched
                  ? 'Country is required'
                  : ''
              }
              //   disabled={
              //     caller === 'payout' &&
              //     beneficiaryFormState === 'selected_existing'
              //   }
            />

            <AppInput
              type="text"
              name="bic"
              id="bic"
              label="BIC"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.bic}
              placeholder="Enter BIC"
              required
              errorMessage={
                formik.touched.bic && formik.errors.bic ? formik.errors.bic : ''
              }
              disabled={
                caller === 'payout' &&
                beneficiaryFormState === 'selected_existing'
              }
            />

            <AppInput
              type="text"
              name="iban"
              id="iban"
              label="IBAN"
              onChange={handleIbanChange}
              onBlur={async (e) => {
                formik.handleBlur(e);
                const response = await validateIban(e.target.value);
                if (formik.values.ibanAccount) {
                  if (response && response.isSuccess) {
                    setIsIbanValid(true);
                    setBeneficiaryPayload({
                      ...beneficiaryPayload,
                      bankName: response.value.country.bank.bankName,
                    });
                    toast.success('Valid IBAN entered.');
                  } else {
                    setIsIbanValid(false);
                    toast.error('IBAN entered is invalid.');
                  }
                }
              }}
              value={formik.values.ibanAccount}
              placeholder={`Enter IBAN (starts with ${formik.values.countryCode || 'country code'})`}
              required
              endIcon={
                validating ? (
                  <Loader width="1.2rem" borderTopColor="border-t-blue-500" />
                ) : isIbanValid ? (
                  <FiCheck />
                ) : null
              }
              errorMessage={
                formik.touched.ibanAccount && formik.errors.ibanAccount
                  ? formik.errors.ibanAccount
                  : ''
              }
              disabled={
                caller === 'payout' &&
                beneficiaryFormState === 'selected_existing'
              }
            />
          </div>
        </div>
        {caller !== 'payout' && (
          <div className="flex gap-4 mt-8">
            <AppButton label="Previous" type="light" clicked={prev} />
            <AppButton
              label="Add Beneficiary"
              type="primary"
              clicked={() => submitForm()}
              disabled={disableForm()}
              loading={loading}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default AddIbanBeneficiary;
